import React, { useEffect, useState, useCallback } from 'react';
import { useParams, Link } from 'react-router-dom';
import { host } from '../script/variables';
import './style/SingleBlog.css';
import { PiHandsClappingDuotone } from 'react-icons/pi';
import Comments from '../Comments';
import Blog from './Blog';
import SEOMeta from '../common/SEOMeta';

/* ─── helpers ─────────────────────────────────────────────── */
const timeAgo = (timestamp) => {
  const diff   = Date.now() - new Date(timestamp);
  const mins   = Math.floor(diff / 60000);
  const hours  = Math.floor(mins  / 60);
  const days   = Math.floor(hours / 24);
  const weeks  = Math.floor(days  / 7);
  const months = Math.floor(days  / 30);
  if (months > 0) return `${months} month${months > 1 ? 's' : ''}`;
  if (weeks  > 0) return `${weeks} week${weeks  > 1 ? 's' : ''}`;
  if (days   > 0) return `${days} day${days    > 1 ? 's' : ''}`;
  if (hours  > 0) return `${hours} hour${hours  > 1 ? 's' : ''}`;
  if (mins   > 0) return `${mins} min${mins    > 1 ? 's' : ''}`;
  return 'just now';
};

const readingTime = (body = []) => {
  const words = body.reduce((acc, block) => {
    const text = block.data?.text || (block.data?.items || []).join(' ');
    return acc + text.split(/\s+/).filter(Boolean).length;
  }, 0);
  return Math.max(1, Math.ceil(words / 200));
};

const objToHtml = ({ type, data }) => {
  if (type === 'header')
    return `<h${data.level} class="sbc-heading">${data.text}</h${data.level}>`;
  if (type === 'paragraph')
    return `<p class="sbc-para">${data.text}</p>`;
  if (type === 'list') {
    const items = (data.items || []).map(i => `<li>${i}</li>`).join('');
    return data.style === 'ordered'
      ? `<ol class="sbc-list">${items}</ol>`
      : `<ul class="sbc-list">${items}</ul>`;
  }
  if (type === 'image')
    return `<figure class="sbc-figure">
      <img src="${data.file?.url}" alt="${data.caption || ''}" loading="lazy" />
      ${data.caption ? `<figcaption>${data.caption}</figcaption>` : ''}
    </figure>`;
  if (type === 'quote')
    return `<blockquote class="sbc-quote">${data.text}${data.caption ? `<cite>— ${data.caption}</cite>` : ''}</blockquote>`;
  if (type === 'code')
    return `<pre class="sbc-code"><code>${data.code}</code></pre>`;
  return '';
};

/* ─── skeleton ────────────────────────────────────────────── */
const Skeleton = () => (
  <div className="sb-skeleton">
    <div className="sbs-cover" />
    <div className="sbs-tag" />
    <div className="sbs-title" />
    <div className="sbs-title sbs-title--short" />
    <div className="sbs-meta" />
    <div className="sbs-line" />
    <div className="sbs-line sbs-line--90" />
    <div className="sbs-line sbs-line--80" />
    <div className="sbs-line" />
    <div className="sbs-line sbs-line--70" />
  </div>
);

/* ─── reading progress bar ────────────────────────────────── */
const ReadingProgress = () => {
  const [pct, setPct] = useState(0);
  useEffect(() => {
    const handler = () => {
      const el      = document.documentElement;
      const scrolled = el.scrollTop || document.body.scrollTop;
      const total   = el.scrollHeight - el.clientHeight;
      setPct(total > 0 ? Math.round((scrolled / total) * 100) : 0);
    };
    window.addEventListener('scroll', handler, { passive: true });
    return () => window.removeEventListener('scroll', handler);
  }, []);
  return (
    <div className="sb-progress" aria-hidden="true">
      <div className="sb-progress__fill" style={{ width: `${pct}%` }} />
    </div>
  );
};

/* ════════════════════════════════════════════════════════════
   MAIN COMPONENT
════════════════════════════════════════════════════════════ */
const SingleBlog = () => {
  const { title } = useParams();

  const [blog,         setBlog]         = useState(null);
  const [loading,      setLoading]      = useState(true);
  const [error,        setError]        = useState('');
  const [isClapped,    setIsClapped]    = useState(false);
  const [claps,        setClaps]        = useState(0);
  const [blogId,       setBlogId]       = useState(null);
  const [commentCount, setCommentCount] = useState(0);
  const [isLogin,      setIsLogin]      = useState(false);
  const [authorBlogs,  setAuthorBlogs]  = useState([]);
  const [tagBlogs,     setTagBlogs]     = useState([]);
  const [clapAnim,     setClapAnim]     = useState(false);
  const [copied,       setCopied]       = useState(false);

  const fetchBlog = useCallback(async () => {
    setLoading(true); setError('');
    try {
      const res = await fetch(`${host}/blog/fetchbytitle`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ title }),
      });
      if (!res.ok) throw new Error('Blog not found');
      const data = await res.json();
      const b = Array.isArray(data) ? data[0] : data;
      if (!b) throw new Error('Blog not found');
      setBlog(b);
      setBlogId(b._id);
      setClaps(b.likeCount || 0);
      setCommentCount(b.commentCount || 0);
    } catch (e) {
      setError(e.message || 'Failed to load blog');
    } finally {
      setLoading(false);
    }
  }, [title]);

  const checkLogin = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      if (!token) return;
      const res = await fetch(`${host}/user/vlogin`, {
        method: 'GET', headers: { Authorization: token },
      });
      if (!res.ok) return;
      setIsLogin(true);
      const cr = await fetch(`${host}/blog/checkisclapped/?title=${encodeURIComponent(title)}`, {
        method: 'GET', headers: { Authorization: token },
      });
      if (cr.ok) setIsClapped(await cr.json());
    } catch { /* silent */ }
  }, [title]);

  const fetchAuthorBlogs = useCallback(async (email) => {
    try {
      const res = await fetch(`${host}/blog/authorblog?em=${encodeURIComponent(email)}`);
      if (res.ok) setAuthorBlogs(await res.json());
    } catch { /* silent */ }
  }, []);

  const fetchTagBlogs = useCallback(async (tags) => {
    try {
      const res = await fetch(`${host}/blog/tagblog`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ tags }),
      });
      if (res.ok) setTagBlogs(await res.json());
    } catch { /* silent */ }
  }, []);

  const handleClap = async () => {
    if (!isLogin || !blog) return;
    setClapAnim(true);
    setTimeout(() => setClapAnim(false), 500);
    // optimistic
    setClaps(c => isClapped ? c - 1 : c + 1);
    setIsClapped(v => !v);
    try {
      const res = await fetch(`${host}/blog/like`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: localStorage.getItem('token'),
        },
        body: JSON.stringify({ bid: blog._id, isClapped }),
      });
      if (!res.ok) {
        setClaps(c => isClapped ? c + 1 : c - 1);
        setIsClapped(v => !v);
      }
    } catch {
      setClaps(c => isClapped ? c + 1 : c - 1);
      setIsClapped(v => !v);
    }
  };

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(window.location.href);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch { /* silent */ }
  };

  useEffect(() => {
    window.scrollTo({ top: 0 });
    fetchBlog();
    checkLogin();
  }, [title, fetchBlog, checkLogin]);

  useEffect(() => {
    if (blog?.authorDetails?.email) fetchAuthorBlogs(blog.authorDetails.email);
    if (blog?.tags?.length)         fetchTagBlogs(blog.tags);
  }, [blog, fetchAuthorBlogs, fetchTagBlogs]);

  const mins        = blog ? readingTime(blog.body || []) : 0;
  const otherAuthor = authorBlogs.filter(b => b._id !== blog?._id);
  const otherTag    = tagBlogs.filter(b => b._id !== blog?._id && !otherAuthor.find(a => a._id === b._id));

  if (loading) return (
    <>
      <ReadingProgress />
      <div className="sb-wrap"><Skeleton /></div>
    </>
  );

  if (error || !blog) return (
    <div className="sb-wrap sb-wrap--error">
      <div className="sb-error">
        <div className="sb-error__icon">📄</div>
        <h2 className="sb-error__title">Blog not found</h2>
        <p className="sb-error__msg">{error || 'This post may have been moved or deleted.'}</p>
        <Link className="sb-btn" to="/blog">← Back to Blog</Link>
      </div>
    </div>
  );

  return (
    <>
      <SEOMeta
        title={blog.title}
        description={blog.excerpt}
        image={blog.coverImage}
        url={`https://www.restropilots.com/blog/${encodeURIComponent(blog.title)}`}
      />

      <ReadingProgress />

      <div className="sb-wrap">

        {blog.coverImage && (
          <div className="sb-cover">
            <img src={blog.coverImage} alt={blog.title} className="sb-cover__img" loading="eager" />
          </div>
        )}

        <article className="sb-article">

          {blog.tags?.length > 0 && (
            <div className="sb-tags">
              {blog.tags.slice(0, 4).map(t => (
                <span className="sb-tag" key={t}>#{t}</span>
              ))}
            </div>
          )}

          <h1 className="sb-title">{blog.title}</h1>
          {blog.excerpt && <p className="sb-excerpt">{blog.excerpt}</p>}

          {blog.authorDetails && (
            <div className="sb-author">
              <img
                className="sb-author__avatar"
                src={blog.authorDetails.image}
                alt={blog.authorDetails.username}
                loading="lazy"
              />
              <div className="sb-author__info">
                <Link className="sb-author__name" to={`/user?id=${blog.authorDetails.email}`}>
                  {blog.authorDetails.username}
                </Link>
                <div className="sb-author__meta">
                  <span>Published {timeAgo(blog.createdAt)} ago</span>
                  <span className="sb-dot">·</span>
                  <span>{mins} min read</span>
                </div>
              </div>
            </div>
          )}

          <hr className="sb-divider" />

          <div
            className="sb-content"
            dangerouslySetInnerHTML={{ __html: (blog.body || []).map(objToHtml).join('') }}
          />

          <hr className="sb-divider" />

          <footer className="sb-footer">
            {blog.tags?.length > 0 && (
              <div className="sb-tags sb-tags--footer">
                {blog.tags.map(t => (
                  <span className="sb-tag" key={t}>#{t}</span>
                ))}
              </div>
            )}
            <div className="sb-actions">
              <button
                className={`sb-clap${isClapped ? ' sb-clap--active' : ''}${clapAnim ? ' sb-clap--pop' : ''}`}
                onClick={handleClap}
                title={isLogin ? (isClapped ? 'Remove clap' : 'Clap!') : 'Sign in to clap'}
              >
                <PiHandsClappingDuotone className="sb-clap__icon" />
                <span className="sb-clap__count">{claps}</span>
              </button>

              <button
                className={`sb-share${copied ? ' sb-share--copied' : ''}`}
                onClick={copyLink}
              >
                {copied ? '✓ Copied!' : '🔗 Share'}
              </button>
            </div>
          </footer>
        </article>

        {blogId && (
          <section className="sb-section">
            <h2 className="sb-section__title">
              Comments
              {commentCount > 0 && (
                <span className="sb-section__badge">{commentCount}</span>
              )}
            </h2>
            <Comments
              postid={blogId}
              setCommentCount={setCommentCount}
              commentCount={commentCount}
              isLogin={isLogin}
              isBlog
            />
          </section>
        )}

        {otherAuthor.length > 0 && (
          <section className="sb-section">
            <h2 className="sb-section__title">
              More from{' '}
              <Link className="sb-section__link" to={`/user?id=${blog.authorDetails?.email}`}>
                {blog.authorDetails?.username}
              </Link>
            </h2>
            <div className="sb-grid">
              {otherAuthor.slice(0, 3).map((b, i) => (
                <Blog key={b._id || i} blog={b} />
              ))}
            </div>
          </section>
        )}

        {otherTag.length > 0 && (
          <section className="sb-section">
            <h2 className="sb-section__title">Similar Topics</h2>
            <div className="sb-grid">
              {otherTag.slice(0, 3).map((b, i) => (
                <Blog key={b._id || i} blog={b} />
              ))}
            </div>
          </section>
        )}

      </div>
    </>
  );
};

export default SingleBlog;